import React from 'react';
import { get, isEmpty } from 'lodash';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { useRouter } from 'next/router';
import { Stack, Typography } from '@mui/material';
import { CardSection, IconMarkerPractitioner } from '@maiia/design-system';
import { styled, css } from '@mui/material/styles';
import Link from '../../atoms/Link/Link';
import Text from '../../atoms/Text/Text';
import { getAddress, getCenterName } from '../../utils/card';
import { useTranslation } from '@/src/i18n';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import Map from '@/components/molecules/Map';
import externalMapUrl from '../../utils/externalMapUrl';

/* JS styling */
const Anchor = styled('a')(
  ({ theme }) => css`
    font-style: italic;
    text-decoration: underline;
    color: ${theme.palette.text.link};
  `,
);
const AccessDetails = styled(Stack, {
  shouldForwardProp: () => true,
})(
  () => css`
    flex-grow: 1;
  `,
);

const MapWrapper = styled('div', {
  shouldForwardProp: () => true,
})(
  ({ theme }) => css`
    #map {
      width: 100%;
      height: ${theme.spacing(25)};

      ${theme.breakpoints.up('md')} {
        width: ${theme.spacing(35)};
        height: ${theme.spacing(35)};
        // Strech map to the edge of the card
        margin-top: calc(
          ${theme.spacing(-2)} - 27px
        ); // 27px the height of the card section title, as the map is being pushed down because of it
        margin-right: ${theme.spacing(-2)};
        margin-bottom: ${theme.spacing(-2)};
        margin-left: ${theme.spacing(2)};
      }
    }
  `,
);

const LinkCenterPrat = styled('a')(
  ({ theme }) => css`
    color: ${theme.palette.text.link};
    font-weight: 500;
    text-decoration: underline;
  `,
);

/* Component */

const mappings = {
  floor: 'accessibility_floor',
  hasElevatorAccess: 'accessibility_elevator',
  hasHandicapAccess: 'accessibility_handicap_access',
  parkingCode: 'accessibility_parking_code',
  digitalCode: 'accessibility_digital_code',
  interphone: 'accessibility_interphone',
};

export const getAccessibilities = (
  officeAccessibilities,
  t,
  options = { showPrivateInfo: true },
) => {
  if (isEmpty(officeAccessibilities)) {
    return {};
  }

  const {
    hasHandicapAccess,
    hasElevatorAccess,
    floor,
    meansOfTransport,
    ...rest
  } = officeAccessibilities;

  return Object.keys({
    hasHandicapAccess,
    hasElevatorAccess,
    floor,
    ...(options?.showPrivateInfo ? { ...rest } : {}),
  }).reduce((result, key) => {
    if (!officeAccessibilities[key]) {
      return result;
    }
    return {
      ...result,
      [key]: t(mappings[key], {
        [key]: officeAccessibilities[key],
      }),
    };
  }, {});
};

export const Accessibility = ({
  card,
  showPrivateInfo = false,
}: {
  card: CardHcd;
  showPrivateInfo: boolean;
}) => {
  const { t } = useTranslation();
  const officeAccessibility = get(
    card,
    'publicInformation.officeInformation.officeAccessibility',
    {},
  );
  const accessibilities = getAccessibilities(officeAccessibility, t, {
    showPrivateInfo,
  });

  const hasAccessibilities = Object.keys(accessibilities).length > 0;

  return (
    <Stack
      className="access__content--infos text-pre"
      spacing={1}
      data-cy="access-content-infos"
    >
      {hasAccessibilities && (
        <div>
          <Text component="h4">{t('useful_informations')}</Text>
          {Object.keys(accessibilities).map(
            key =>
              accessibilities[key] && <p key={key}>{accessibilities[key]}</p>,
          )}
        </div>
      )}

      {get(officeAccessibility, 'meansOfTransport') && (
        <div>
          <Text component="h4">{t('accessibility_transportation_means')}</Text>
          <p>{get(officeAccessibility, 'meansOfTransport')}</p>
        </div>
      )}
    </Stack>
  );
};

const AccessContent = ({ card }: { card: CardHcd }) => {
  const urlForMap = externalMapUrl(card);
  const { t } = useTranslation();
  const router = useRouter();
  const isInSecretCenter =
    card?.type === 'PRACTITIONER' && card?.center?.isSecret === true;
  const isCenter = card.type === 'CENTER';

  return (
    <CardSection icon={<IconMarkerPractitioner />} title={t('access')}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <AccessDetails spacing={1}>
          <div data-testid="access-link-center-prat">
            <Typography
              component="h3"
              variant="h6"
              fontWeight={500}
              data-cy="link-center-prat"
            >
              {isCenter ? (
                getCenterName(card)
              ) : (
                <Link
                  href={
                    card?.center?.url && !isInSecretCenter
                      ? card?.center?.url
                      : router.asPath
                  }
                  passHref
                  legacyBehavior
                >
                  <LinkCenterPrat
                    onClick={() => {
                      analyticsEvent({
                        category: 'PractitionerOrCenterPage',
                        action: 'ClickAddressViewCenter',
                      });
                    }}
                  >
                    {getCenterName(card)}
                  </LinkCenterPrat>
                </Link>
              )}
            </Typography>
            <p>{getAddress(card)}</p>
          </div>
          <Accessibility card={card} showPrivateInfo={false} />
          <Anchor target="_blank" href={urlForMap}>
            {t(urlForMap.includes('apple') ? 'see_map_on_ios' : 'see_map')}
          </Anchor>
        </AccessDetails>
        {card?.publicInformation?.address?.location && (
          <MapWrapper>
            <Map items={[card]} showPharmacies enableTrackEvents />
          </MapWrapper>
        )}
      </Stack>
    </CardSection>
  );
};

export default AccessContent;
